import React from "react";
import { Popover, Button, Typography, Box, Divider } from "@mui/material";
import { Link, CalendarClock, SquareCheckBig, Check } from "lucide-react";

export type ActionIconType = 'linked' | 'moved' | 'related';
type TriState = 'none' | 'include' | 'exclude';

export interface ActionsFilterProps {
    anchorEl?: HTMLElement | null;
    open?: boolean;
    onClose?: () => void;
    // "Show only rows with" - a row must match at least one checked type.
    selected: Set<ActionIconType>;
    onChange: (next: Set<ActionIconType>) => void;
    // "Show only rows without" - a row must match none of the checked types.
    excluded: Set<ActionIconType>;
    onChangeExcluded: (next: Set<ActionIconType>) => void;
}

const OPTIONS: { value: ActionIconType; label: string; icon: React.ReactNode }[] = [
    { value: 'linked', label: 'Linked', icon: <Link size={15} color="var(--color-primary)" /> },
    { value: 'moved', label: 'Moved', icon: <CalendarClock size={15} color="var(--color-provision-text)" /> },
    { value: 'related', label: 'Related', icon: <SquareCheckBig size={15} color="var(--color-primary)" /> },
];

// A three-way toggle per icon (rather than two separate checkbox lists for
// "with" and "without") - cycles neutral -> must-have -> must-not-have ->
// neutral on each click, so there's only ever one control per row instead
// of every row needing to be scanned twice to see its current state.
const STATE_STYLE: Record<TriState, { bg: string; border: string; icon: React.ReactNode }> = {
    none: { bg: 'transparent', border: '#c7c7c7ff', icon: null },
    include: { bg: '#00b69b', border: '#00b69b', icon: <Check size={13} color="#fff" strokeWidth={3} /> },
    exclude: { bg: 'transparent', border: '#e05252', icon: <Box sx={{ width: 8, height: 8, borderRadius: '1.5px', background: '#e05252' }} /> },
};

const ActionsFilter: React.FC<ActionsFilterProps> = ({ anchorEl, open, onClose, selected, onChange, excluded, onChangeExcluded }) => {
    const stateOf = (value: ActionIconType): TriState =>
        selected.has(value) ? 'include' : excluded.has(value) ? 'exclude' : 'none';

    const cycle = (value: ActionIconType) => {
        const state = stateOf(value);
        if (state === 'none') {
            onChange(new Set(selected).add(value));
        } else if (state === 'include') {
            const nextSelected = new Set(selected);
            nextSelected.delete(value);
            onChange(nextSelected);
            onChangeExcluded(new Set(excluded).add(value));
        } else {
            const nextExcluded = new Set(excluded);
            nextExcluded.delete(value);
            onChangeExcluded(nextExcluded);
        }
    };

    return (
        <Popover
            open={!!open}
            anchorEl={anchorEl}
            onClose={onClose}
            anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
            PaperProps={{
                sx: {
                    borderRadius: 2,
                    boxShadow: 3,
                    p: 2,
                    minWidth: 220,
                },
            }}
        >
            <Typography sx={{ fontWeight: 700, fontSize: 18 }}>Filter</Typography>
            <Divider sx={{ my: 1 }} />
            <Box display="flex" flexDirection="column" gap={0.5} mt={1}>
                {OPTIONS.map(option => {
                    const state = stateOf(option.value);
                    const style = STATE_STYLE[state];
                    return (
                        <Box
                            key={option.value}
                            onClick={() => cycle(option.value)}
                            display="flex"
                            alignItems="center"
                            gap={1}
                            sx={{
                                cursor: 'pointer',
                                borderRadius: 1,
                                py: 0.5,
                                px: 0.5,
                                '&:hover': { background: 'var(--color-container-background)' },
                            }}
                        >
                            <Box
                                sx={{
                                    width: 18,
                                    height: 18,
                                    flexShrink: 0,
                                    borderRadius: 0.75,
                                    border: `2.5px solid ${style.border}`,
                                    background: style.bg,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                }}
                            >
                                {style.icon}
                            </Box>
                            {option.icon}
                            <Typography sx={{ fontSize: 13, fontWeight: state === 'exclude' ? 600 : 400 }}>
                                {state === 'exclude' ? `Not ${option.label}` : state === 'none' ? `${option.label}?` : option.label}
                            </Typography>
                        </Box>
                    );
                })}
            </Box>
            <Box display="flex" gap={1} mt={2}>
                <Button
                    fullWidth
                    variant="contained"
                    sx={{ background: 'var(--color-container-background)', color: 'var(--color-text-muted)', borderRadius: 2, fontWeight: 600, boxShadow: 'none', '&:hover': { background: 'var(--color-container-background)' } }}
                    onClick={() => {
                        onChange(new Set());
                        onChangeExcluded(new Set());
                    }}
                >
                    Clear
                </Button>
            </Box>
        </Popover>
    );
};

export default ActionsFilter;
